<template>
  <div :class="ns.b()">
    <transfer-panel ref="leftPanel" :data="sourceData" :option-render="optionRender"
      :placeholder="panelFilterPlaceholder" :title="leftPanelTitle" :filterable="filterable" :format="format"
      :filter-method="filterMethod" :default-checked="leftDefaultChecked" :props="props.props"
      @checked-change="onSourceCheckedChange">
      <template #empty>
        <slot name="left-empty" />
      </template>
      <slot name="left-footer" />
    </transfer-panel>
    <div :class="ns.e('buttons')">
      <z-button type="primary" :class="[ns.e('button'), ns.is('with-texts', hasButtonTexts)]"
        :disabled="isEmpty(checkedState.rightChecked)" @click="addToLeft">
        <z-icon><arrow-left /></z-icon>
        <span v-if="!isUndefined(buttonTexts[0])">{{ buttonTexts[0] }}</span>
      </z-button>
      <z-button type="primary" :class="[ns.e('button'), ns.is('with-texts', hasButtonTexts)]"
        :disabled="isEmpty(checkedState.leftChecked)" @click="addToRight">
        <span v-if="!isUndefined(buttonTexts[1])">{{ buttonTexts[1] }}</span>
        <z-icon><arrow-right /></z-icon>
      </z-button>
    </div>
    <transfer-panel ref="rightPanel" :data="targetData" :option-render="optionRender"
      :placeholder="panelFilterPlaceholder" :filterable="filterable" :format="format" :filter-method="filterMethod"
      :title="rightPanelTitle" :default-checked="rightDefaultChecked" :props="props.props"
      @checked-change="onTargetCheckedChange">
      <template #empty>
        <slot name="right-empty" />
      </template>
      <slot name="right-footer" />
    </transfer-panel>
  </div>
</template>

<script lang="ts" setup generic="T extends TransferDataItem = TransferDataItem">
import { Comment, computed, h, reactive, ref, watch } from 'vue'
import { NOOP, isEmpty, isUndefined } from '@zoryn/utils'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { ZButton } from '@zoryn/components/button'
import { ZIcon } from '@zoryn/components/icon'
import { useFormItem } from '@zoryn/components/form'
import { ArrowLeft, ArrowRight } from '@zoryn/icons'
import { transferEmits } from './transfer'
import {
  useCheckedChange,
  useComputedData,
  useMove,
  usePropsAlias,
} from './composables'
import TransferPanel from './transfer-panel.vue'

import type { VNode } from 'vue'
import type {
  TransferCheckedState,
  TransferDataItem,
  TransferDirection,
  TransferProps,
} from './transfer'
import type { TransferPanelInstance } from './transfer-panel'

defineOptions({
  name: 'ZTransfer',
})

const props = withDefaults(defineProps<TransferProps<T>>(), {
  data: () => [],
  titles: () => [] as unknown as [string, string],
  buttonTexts: () => [] as unknown as [string, string],
  leftDefaultChecked: () => [],
  rightDefaultChecked: () => [],
  modelValue: () => [],
  format: () => ({}),
  props: () => ({
    label: 'label',
    key: 'key',
    disabled: 'disabled',
  }),
  targetOrder: 'original',
  validateEvent: true,
})
const emit = defineEmits(transferEmits)
const slots = defineSlots<{
  default?: (props: { option: T }) => VNode[]
  'left-empty'?: () => VNode[]
  'left-footer'?: () => VNode[]
  'right-empty'?: () => VNode[]
  'right-footer'?: () => VNode[]
}>()

const { t } = useLocale()
const ns = useNamespace('transfer')
const { formItem } = useFormItem()

const checkedState = reactive<TransferCheckedState>({
  leftChecked: [],
  rightChecked: [],
})

const propsAlias = usePropsAlias(props)

const { sourceData, targetData } = useComputedData(props)

const { onSourceCheckedChange, onTargetCheckedChange } = useCheckedChange(
  checkedState,
  emit
)

const { addToLeft, addToRight } = useMove(props, checkedState, emit)

const leftPanel = ref<TransferPanelInstance>()
const rightPanel = ref<TransferPanelInstance>()

const clearQuery = (which: TransferDirection) => {
  switch (which) {
    case 'left':
      leftPanel.value!.query = ''
      break
    case 'right':
      rightPanel.value!.query = ''
      break
  }
}

const hasButtonTexts = computed(() => props.buttonTexts.length === 2)

const leftPanelTitle = computed(
  () => props.titles[0] || t('z.transfer.titles.0')
)

const rightPanelTitle = computed(
  () => props.titles[1] || t('z.transfer.titles.1')
)

const panelFilterPlaceholder = computed(
  () => props.filterPlaceholder || t('z.transfer.filterPlaceholder')
)

watch(
  () => props.modelValue,
  () => {
    if (props.validateEvent) {
      formItem?.validate?.('change').catch(NOOP)
    }
  }
)

const optionRender = computed(() => (option: T) => {
  if (props.renderContent) return props.renderContent(h, option)

  const defaultSlotVNodes = (slots.default?.({ option }) || []).filter(
    (node) => node.type !== Comment
  )
  if (defaultSlotVNodes.length) {
    return defaultSlotVNodes
  }

  return h(
    'span',
    option[propsAlias.value.label] || option[propsAlias.value.key]
  )
})

defineExpose({
  /** @description clear the filter keyword of a certain panel */
  clearQuery,
  /** @description left panel ref */
  leftPanel,
  /** @description right panel ref */
  rightPanel,
})
</script>
